import type { ReceiptData, PosSettings } from '@/types';
import { usePrinterStore } from '@/store/printerStore';
import {
  isThermalPrinterConfigured,
  printEscPosBytes,
} from '@/hooks/usePrinterConnection';
import { receiptToDantsu } from '@/utils/receiptToDantsu';
import { convertDantsuToEscPos } from '@/utils/thermalPrinterUtils';
import { formatMoney } from '@/utils/currency';
import { receiptLinePromo, receiptPromoTotals, showReceiptDiscountPrint } from '@/utils/receiptPromo';
import i18n from '@/i18n';
import { getPoweredByI18nKey } from '@/utils/themeDomain';

export type SilentPrintResult = { ok: true } | { ok: false; error: string };

/**
 * Silent thermal print (USB Print Helper or Bluetooth/serial).
 * Does NOT open a browser print dialog — callers should toast on failure.
 */
export async function silentPrintReceipt(
  inv: ReceiptData,
  _settings?: unknown,
  companyName?: string,
  posSettings?: PosSettings,
): Promise<SilentPrintResult> {
  const { settings } = usePrinterStore.getState();

  if (!isThermalPrinterConfigured(settings)) {
    return {
      ok: false,
      error: 'No printer configured. Open Settings → Printer and set up USB or Bluetooth.',
    };
  }

  try {
    const dantsu = receiptToDantsu(inv, {
      companyName: inv.company_name || companyName || 'Sale Desk',
      paperWidth: settings.paperWidth === 58 ? '58mm' : '80mm',
      posSettings,
    });
    const bytes = await convertDantsuToEscPos(dantsu, settings.feedLines, settings.autoCut);
    // Bluetooth needs a user gesture for the device picker when not already connected.
    // Complete Sale provides that gesture (see primePrinterForAutoPrint). Still allow
    // picker here as fallback. Never fall back to window.print().
    const allowPicker = settings.connectionType === 'bluetooth';
    await printEscPosBytes(bytes, { allowPicker, settings });
    return { ok: true };
  } catch (err: unknown) {
    const message =
      err instanceof Error ? err.message : 'Could not print receipt to the printer.';
    console.warn('Silent print failed', err);
    return { ok: false, error: message };
  }
}

/**
 * Browser / fallback print: plain 80mm monospace receipt.
 * Use for explicit user-initiated Print when thermal path is unavailable.
 */
export function browserPrintThermalReceipt(
  inv: ReceiptData,
  companyName?: string,
  posSettings?: PosSettings,
): void {
  const store = inv.company_name || companyName || 'Sale Desk';
  const d = new Date(inv.creation || inv.invoice_date || Date.now());
  const dateTimeStr =
    d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ', ' +
    d.toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const money = (n: number) => formatMoney(n || 0);
  const displayDiscount = (inv.discount_amount || 0) + (inv.write_off_amount || 0);
  const showPromoPrint = showReceiptDiscountPrint(posSettings);
  const promoTotals = receiptPromoTotals(inv);
  const showPromoRow = showPromoPrint && promoTotals.promo > 0;
  const servedBy = inv.cashier_name || inv.cashier || 'Cashier';
  const payments = inv.payments?.length
    ? inv.payments
    : [{ payment_method: 'Cash', amount: inv.paid_amount }];

  const itemRows = (inv.items || [])
    .map((item) => {
      const name = `${item.item_name}${item.variant_name ? ` - ${item.variant_name}` : ''}`;
      const line = receiptLinePromo(item);
      const rateCell = showPromoPrint && line.hasPromo
        ? `<s>${money(line.originalRate)}</s> ${money(line.chargedRate)}`
        : money(line.chargedRate);
      const amtCell = showPromoPrint && line.hasPromo
        ? `<s>${money(line.originalAmount)}</s> ${money(line.chargedAmount)}`
        : money(line.chargedAmount);
      return `<tr>
        <td>${esc(name)}</td>
        <td class="num">${item.quantity}</td>
        <td class="num">${rateCell}</td>
        <td class="num">${amtCell}</td>
      </tr>`;
    })
    .join('');

  const paymentRows = payments
    .map(
      (p) =>
        `<div class="row"><span>${esc(p.payment_method)}</span><span>${money(p.amount)}</span></div>`,
    )
    .join('');

  const isPk = (posSettings?.country || 'PK') === 'PK';
  const sellerIdLines: string[] = [];
  if (isPk && posSettings?.fbr_ntn) sellerIdLines.push(`NTN: ${posSettings.fbr_ntn}`);
  if (isPk && posSettings?.fbr_strn) sellerIdLines.push(`GST/STRN: ${posSettings.fbr_strn}`);
  if (!isPk && posSettings?.tax_registration_id) {
    const label = posSettings.country === 'US' ? 'EIN' : posSettings.country === 'SA' ? 'VAT / TRN' : posSettings.country === 'AE' ? 'TRN' : 'VAT';
    sellerIdLines.push(`${label}: ${posSettings.tax_registration_id}`);
  } else if (posSettings?.country === 'SA' && posSettings?.zatca_vat_number) {
    sellerIdLines.push(`VAT / TRN: ${posSettings.zatca_vat_number}`);
  } else if (posSettings?.country === 'AE' && posSettings?.uae_trn) {
    sellerIdLines.push(`TRN: ${posSettings.uae_trn}`);
  }

  const html = `
    <div class="ticket">
      <div class="center bold big">${esc(store)}</div>
      ${sellerIdLines.map((l) => `<div class="center small">${esc(l)}</div>`).join('')}
      ${inv.company_address ? `<div class="center small">${esc(inv.company_address)}</div>` : ''}
      ${inv.company_phone ? `<div class="center small">Ph: ${esc(inv.company_phone)}</div>` : ''}
      <div class="line"></div>
      <div class="row"><span>Invoice No.</span><span>${esc(inv.name)}</span></div>
      <div class="row"><span>Date &amp; Time</span><span>${esc(dateTimeStr)}</span></div>
      <div class="row"><span>Customer</span><span>${esc(inv.customer_name || 'Walk in')}</span></div>
      <div class="row"><span>Served By</span><span>${esc(servedBy)}</span></div>
      <div class="line"></div>
      <table class="items">
        <thead>
          <tr><th>Item</th><th class="num">Qty</th><th class="num">Rate</th><th class="num">Amt</th></tr>
        </thead>
        <tbody>${itemRows}</tbody>
      </table>
      <div class="line"></div>
      <div class="row"><span>Subtotal</span><span>${money(showPromoRow ? promoTotals.original : promoTotals.charged)}</span></div>
      ${showPromoRow ? `<div class="row"><span>Promotions</span><span>-${money(promoTotals.promo)}</span></div>` : ''}
      ${displayDiscount > 0 && showPromoPrint ? `<div class="row"><span>Discount</span><span>-${money(displayDiscount)}</span></div>` : ''}
      ${(inv.tax_amount || 0) > 0 && (!posSettings || posSettings.show_tax_in_invoice !== false) ? `<div class="row"><span>${(posSettings?.tax_label || 'Tax').trim() || 'Tax'}</span><span>${money(inv.tax_amount)}</span></div>` : ''}
      <div class="row bold"><span>TOTAL</span><span>${money(inv.net_total)}</span></div>
      <div class="line"></div>
      ${paymentRows}
      <div class="row bold"><span>PAID</span><span>${money(inv.paid_amount)}</span></div>
      ${(inv.balance_amount || 0) > 0 ? `<div class="row"><span>BALANCE</span><span>${money(inv.balance_amount)}</span></div>` : ''}
      <div class="line"></div>
      <div class="center">Thank you for your business!</div>
      <div class="center small">${i18n.t(getPoweredByI18nKey())}</div>
    </div>
  `;

  openPrintWindow(html, inv.name);
}

/** @deprecated Prefer browserPrintThermalReceipt — kept for any existing callers */
export function browserPrintReceipt(receiptHtml: string, invoiceName: string): void {
  openPrintWindow(receiptHtml, invoiceName);
}

export function openThermalPrintWindow(bodyHtml: string, title: string): void {
  openPrintWindow(bodyHtml, title);
}

function esc(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function openPrintWindow(bodyHtml: string, invoiceName: string): void {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Receipt - ${invoiceName}</title>
      <style>
        * { box-sizing: border-box; }
        body {
          margin: 0;
          padding: 8px;
          font-family: 'Courier New', Courier, monospace;
          color: #000;
          background: #fff;
        }
        .ticket {
          width: 72mm;
          max-width: 100%;
          margin: 0 auto;
          font-size: 12px;
          line-height: 1.35;
        }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .big { font-size: 15px; margin-bottom: 2px; }
        .small { font-size: 11px; }
        .line {
          border-top: 1px dashed #000;
          margin: 6px 0;
        }
        .row {
          display: flex;
          justify-content: space-between;
          gap: 8px;
          margin: 2px 0;
        }
        table.items {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 11px;
        }
        table.items th, table.items td {
          padding: 2px 4px;
          vertical-align: top;
          word-break: break-word;
        }
        table.items th:first-child,
        table.items td:first-child {
          padding-left: 0;
        }
        table.items th:last-child,
        table.items td:last-child {
          padding-right: 0;
        }
        table.items th { text-align: left; border-bottom: 1px solid #000; }
        table.items .num, .num { text-align: right; white-space: nowrap; }
        table.items .status { text-align: right; white-space: nowrap; }
        table.items col.col-item { width: 52%; }
        table.items col.col-qty { width: 14%; }
        table.items col.col-amt { width: 34%; }
        table.items col.col-invoice { width: 38%; }
        table.items col.col-amount { width: 34%; }
        table.items col.col-status { width: 28%; }
        table.items col.col-return { width: 32%; }
        table.items col.col-ret-inv { width: 34%; }
        table.items col.col-ret-amt { width: 34%; }
        @media print {
          body { padding: 0; }
          .ticket { width: 72mm; }
        }
      </style>
    </head>
    <body>
      ${bodyHtml}
      <script>window.onload = function() { window.print(); setTimeout(() => window.close(), 500); };</script>
    </body>
    </html>
  `);
  printWindow.document.close();
}
